import React from "react";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";

type BaseProps = {
  variant?: "primary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
  noHover?: boolean;
};

type CustomButtonProps =
  | (BaseProps & { asChild?: false } & React.ButtonHTMLAttributes<HTMLButtonElement>)
  | (BaseProps & { asChild: true } & React.AnchorHTMLAttributes<HTMLAnchorElement>);

export function CustomButton({ 
  variant = "primary", 
  size = "md", 
  className, 
  children, 
  noHover = false,
  asChild = false,
  ...props 
}: CustomButtonProps) {
  const baseStyles = noHover 
    ? "relative inline-flex items-center justify-center font-medium transition-all duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
    : "relative inline-flex items-center justify-center font-medium transition-all duration-300 ease-in-out hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden";
  
  const variants = {
    primary: noHover 
      ? "bg-ss-light-green text-black! font-semibold rounded-3xl shadow"
      : "bg-ss-light-green text-black! font-semibold rounded-3xl shadow hover:opacity-90",
    outline: "border lg:border-2 border-ss-light-green font-semibold bg-white text-black! rounded-3xl shadow-sm",
    ghost: noHover
      ? "border-2 border-ss-light-green text-white! font-semibold bg-transparent rounded-3xl"
      : "border-2 border-ss-light-green text-white! font-semibold bg-transparent rounded-3xl"
  };
  
    const sizes = {
    sm: "px-2 py-1 text-xs md:px-3 md:py-1 md:text-sm",
    md: "px-3 py-1.5 text-xs md:px-4 md:py-2 md:text-base! lg:px-8 lg:py-3",
    lg: "px-5 py-2 text-sm md:px-8 md:py-3 md:text-base"
  };
  
  const Comp: any = asChild ? Slot : "button";

  // When asChild is true, Slot requires exactly one child element
  if (asChild) {
    return (
      <Comp
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        {...props}
      >
        {children}
      </Comp>
    );
  }

  return (
    <Comp
      className={cn(baseStyles, variants[variant], sizes[size], className)}
      {...props}
    >
      {/* Grainy texture overlay - only for non-outline variants */}
      {variant !== "outline" && (
        <div 
          className="min-w-fit w-fit max-w-fit cursor-pointer absolute inset-0 opacity-40 pointer-events-none rounded-3xl" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='6' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            backgroundSize: '150px 150px'
          }}
        />
      )}
      <span className="relative z-10 flex flex-row items-center">{children}</span>
    </Comp>
  );
}
